import { useMutation, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { Button } from 'Components/Buttons';
import { Layer, Text } from 'grommet';
import { memo } from 'preact/compat';
import { useCallback, useState } from 'preact/hooks';

import { useLatestActiveTime } from '@/hooks/useLatestActiveDateTime';
import { getUserProductsQueryOptions } from '@/queries/products';
import { GetUserProductsResponse } from '@/queries/products/types';
import { deleteUserMutationFn } from '@/queries/users';

import style from '../style.scss';
import { Content, Header, HeaderTitle, PrimaryButton, Section } from './Section';

const DeleteAccountSection = () => {
  const [showModal, setShowModal] = useState(false);
  const [now] = useLatestActiveTime();

  const {
    mutate: deleteUser,
    isPending,
    isSuccess,
    isError,
    reset,
  } = useMutation({
    mutationFn: deleteUserMutationFn,
    onSuccess: () => {
      setTimeout(() => {
        // navigating to '/map' will automatically logout the user:
        window.location.href = `/map`;
      }, 3000);
    },
  });

  const selectHasActiveSubscription = useCallback(
    (data: GetUserProductsResponse) => {
      return data.list.some(
        (product) =>
          (product.productKind === 'recurring' || product.productKind === 'sponsorship') &&
          Date.parse(product.validUntil) > now &&
          !product.cancellationInfo,
      );
    },
    [now],
  );

  // we check for active subscription even the user didn't start the deletion process yet
  // because we already have the data in the query client from another section in the same page
  const { data: hasActiveSubscription } = useQuery({
    ...getUserProductsQueryOptions(),
    select: selectHasActiveSubscription,
  });

  const onCloseModal = () => {
    if (isPending) return;
    setShowModal(false);
  };

  return (
    <>
      {showModal && (
        <Layer
          position="center"
          modal
          onClickOutside={onCloseModal}
          onEsc={onCloseModal}
          className="!w-[calc(100vw-2rem)] !max-w-[406px] !rounded-2xl"
        >
          <div className="flex w-full flex-col items-center p-6">
            <div className="flex w-full flex-col items-center pb-4">
              <h5 className="preflight preflight-h5 text-2xl font-bold">
                {hasActiveSubscription ? 'Conflicting Membership' : 'Confirm Data Deletion'}
              </h5>
              <div className={clsx(style.line, 'mt-1 h-[3px] w-full max-w-80 bg-[#BE123C]')} />
            </div>
            <p className="preflight preflight-p text-center text-base text-grey-6">
              {hasActiveSubscription ? (
                <>
                  You cannot delete your personal data while you have a <strong>running active subscription</strong>.
                  <br />
                  <br />
                  If you want to delete your data and account, manage your plans first.
                </>
              ) : (
                <>
                  Are you sure you want to delete your personal data? This will also delete your account.
                  <br />
                  <br />
                  All your personal data will be deleted from our servers, and will not be retrievable.
                </>
              )}
            </p>
            {isError && (
              <Text weight="bolder" textAlign="center" size="large" color="var(--red)" margin={{ top: '20px' }}>
                An error occurred while deleting your account. Please try again or contact us at support@zoolife.tv if the
                problem persists.
              </Text>
            )}
            {isSuccess && (
              <Text weight="bolder" textAlign="center" size="large" color="var(--primaryGreen)" margin={{ top: '20px' }}>
                Your account has been successfully deleted. Now you will be logged out... <br />
                <br />
                Thank you for your time with us.
              </Text>
            )}
            <Button
              disabled={isPending || isSuccess}
              size="manual"
              className={clsx(
                'mt-4 w-full p-3 text-base font-bold',
                !hasActiveSubscription && 'bg-[#BE123C] hover:bg-[#BE123C]/80',
              )}
              onClick={() => {
                if (hasActiveSubscription) {
                  setShowModal(false);
                  reset();
                } else {
                  deleteUser();
                }
              }}
            >
              {hasActiveSubscription ? 'Manage Membership' : 'Delete My Data'}
            </Button>
            <button
              disabled={isPending}
              type="button"
              className="preflight preflight-button mt-4 p-1 text-grey-6"
              onClick={onCloseModal}
            >
              <span className="underline">{hasActiveSubscription ? 'Dismiss' : 'Cancel'}</span>
            </button>
          </div>
        </Layer>
      )}
      <Section>
        <Header className="border-[#BE123C]">
          <div className="flex flex-col desktop:flex-row desktop:items-center desktop:justify-between">
            <HeaderTitle>Delete My Personal Data</HeaderTitle>
            <div className="flex justify-end mobile:order-first mobile:mb-2 mobile:w-full">
              <span className="rounded-[4px] bg-[#BE123C] px-1 py-0.5 text-[10px]/3 font-medium uppercase text-white">
                DANGER ZONE
              </span>
            </div>
          </div>
          <p className="preflight preflight-p mt-1 text-sm text-stone-500">
            You can delete your account & data anytime when you have no active subscription.{' '}
            <a
              data-native
              href="/terms-and-conditions"
              target="_blank"
              rel="noopener noreferrer"
              className="preflight preflight-a text-black underline"
            >
              Read our Data Policy
            </a>
          </p>
        </Header>
        <Content className="pb-10 pt-5 desktop:pt-10">
          <div className="flex justify-between gap-10 mobile:flex-col desktop:items-center desktop:gap-4">
            <h6 className="preflight preflight-h6 text-base text-grey-2">
              Are you sure? <span className="text-black">Deleting your data will also delete your account.</span>
            </h6>
            <PrimaryButton className="min-w-[218px] bg-[#BE123C] hover:bg-[#BE123C]/80" onClick={() => setShowModal(true)}>
              Delete My Data
            </PrimaryButton>
          </div>
        </Content>
      </Section>
    </>
  );
};

export default memo(DeleteAccountSection);
